import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { Org as ApiOrg, fetchOrg as apiFetchOrg } from 'api/orgs';

import { setError } from '../ducks/error';

export type Org = ApiOrg & {
  isFetching: boolean;
  isUnpublished: boolean;
};

export type State = {
  byId: Record<string, Partial<Org>>;
};

export const fetchOrg = createAsyncThunk<ApiOrg, string>(
  'orgs/fetchOrg',
  // eslint-disable-next-line consistent-return
  async (orgId: string, thunkAPI) => {
    try {
      const fetchedOrg = await apiFetchOrg(orgId);
      return fetchedOrg as Org;
    } catch (e) {
      if (e.statusCode !== 404) {
        thunkAPI.dispatch(setError(e));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        thunkAPI.dispatch(setOrgUnpublished(orgId));
      }
    }
  }
);

export const { actions, reducer } = createSlice({
  name: 'orgs',
  initialState: {
    byId: {},
  },
  reducers: {
    setOrgUnpublished: (state: State, action) => {
      state.byId[action.payload].isFetching = false;
      state.byId[action.payload].isUnpublished = true;
    },
    updateOrg: (state, action) => {
      state.byId[action.payload.id] = action.payload;
    },
    partialUpdateOrg: (state, action) => ({
      ...state,
      byId: {
        ...state.byId,
        [action.payload.id]: {
          ...state.byId[action.payload.id],
          ...action.payload,
        },
      },
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrg.pending, (state: State, action) => {
        state.byId[action.meta.arg] = {
          id: action.meta.arg,
          isFetching: true,
        };
      })
      .addCase(fetchOrg.fulfilled, (state, action) => {
        state.byId[action.meta.arg].isFetching = false;

        // If a listing is unpublished, the action technically
        // is fulfilled, but there is no payload, so we
        // don't set it here, letting the `setOrgUnpublished`
        // action handle the state change.
        if (action.payload) {
          state.byId[action.meta.arg] = action.payload;
        }
      });
  },
});

export const { updateOrg, setOrgUnpublished, partialUpdateOrg } = actions;
